/**
 * Link Component Definition
 *
 * A clickable link element that renders as an <a> tag.
 * Supports external URLs, internal page links, and full styling.
 */

import { resolveTypography } from '../../renderer/StyleResolver';

export const linkComponent = {
  type: 'link',
  label: 'Link',
  icon: 'Link',
  description: 'Clickable link with text content',
  category: 'content',

  canHaveChildren: false,

  resolveStyles(props) {
    return {
      ...resolveTypography(props),
    };
  },

  defaultProps: {
    // Content
    text: 'Link',
    url: '',
    openInNewTab: false,

    // Typography
    fontFamily: 'System Default',
    fontSize: { base: { value: 16, unit: 'px' }, tablet: null, desktop: null },
    fontWeight: { base: 'normal', tablet: null, desktop: null },
    lineHeight: { base: { value: 1.5, unit: '' }, tablet: null, desktop: null },
    letterSpacing: { base: { value: 0, unit: 'px' }, tablet: null, desktop: null },
    color: '#2563eb',
    textAlign: { base: 'left', tablet: null, desktop: null },
    textDecoration: { base: 'underline', tablet: null, desktop: null },

    // Spacing
    padding: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },

    // Border
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 0,

    // Advanced
    visibility: { base: true, tablet: null, desktop: null },

    // Positioning
    position: 'static',
    top: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    right: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    bottom: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    left: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    zIndex: 'auto',

    // Custom CSS
    customCSS: '',

    // Flex child (when inside a flex parent) — responsive
    flexChild: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Content category
    {
      key: 'text',
      type: 'text',
      label: 'Link Text',
      category: 'Content',
      default: 'Link',
    },
    {
      key: 'url',
      type: 'link',
      label: 'Link URL',
      category: 'Content',
      placeholder: 'https://...',
    },
    {
      key: 'openInNewTab',
      type: 'checkbox',
      label: 'Open in new tab',
      category: 'Content',
      default: false,
    },

    // Typography category
    {
      key: 'fontFamily',
      type: 'fontFamily',
      label: 'Font Family',
      category: 'Typography',
      default: 'System Default',
    },
    {
      key: 'fontSize',
      type: 'responsiveDimension',
      label: 'Font Size',
      category: 'Typography',
      min: 8,
      max: 200,
      units: ['px', 'rem', 'em'],
      default: { base: { value: 16, unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'fontWeight',
      type: 'responsiveSelect',
      label: 'Weight',
      category: 'Typography',
      default: { base: 'normal', tablet: null, desktop: null },
      options: [
        { value: 'normal', label: 'Normal (400)' },
        { value: '500', label: 'Medium (500)' },
        { value: '600', label: 'Semibold (600)' },
        { value: 'bold', label: 'Bold (700)' },
      ],
    },
    {
      key: 'lineHeight',
      type: 'responsiveDimension',
      label: 'Line Height',
      category: 'Typography',
      min: 0.5,
      max: 5,
      step: 0.1,
      units: ['', 'px', 'em'],
      default: { base: { value: 1.5, unit: '' }, tablet: null, desktop: null },
    },
    {
      key: 'letterSpacing',
      type: 'responsiveDimension',
      label: 'Letter Spacing',
      category: 'Typography',
      min: -5,
      max: 20,
      units: ['px', 'em'],
      default: { base: { value: 0, unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'color',
      type: 'color',
      label: 'Text Color',
      category: 'Typography',
      default: '#2563eb',
    },
    {
      key: 'textAlign',
      type: 'responsiveSelect',
      label: 'Alignment',
      category: 'Typography',
      default: { base: 'left', tablet: null, desktop: null },
      options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ],
    },
    {
      key: 'textDecoration',
      type: 'responsiveSelect',
      label: 'Decoration',
      category: 'Typography',
      default: { base: 'underline', tablet: null, desktop: null },
      options: [
        { value: 'none', label: 'None' },
        { value: 'underline', label: 'Underline' },
      ],
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
    },

    // Border category
    {
      key: 'borderWidth',
      type: 'number',
      label: 'Border Width (px)',
      category: 'Border',
      min: 0,
      max: 10,
      default: 0,
    },
    {
      key: 'borderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
      ],
    },
    {
      key: 'borderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Border',
      min: 0,
      max: 50,
      default: 0,
    },

    // Advanced category
    {
      key: 'visibility',
      type: 'responsiveVisibility',
      label: 'Visibility',
      category: 'Advanced',
      default: { base: true, tablet: null, desktop: null },
    },

    // Position category
    {
      key: 'position',
      type: 'select',
      label: 'Position',
      category: 'Position',
      default: 'static',
      options: [
        { value: 'static', label: 'Static (Normal)' },
        { value: 'relative', label: 'Relative' },
        { value: 'absolute', label: 'Absolute' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'sticky', label: 'Sticky' },
      ],
    },
    {
      key: 'top',
      type: 'responsiveDimension',
      label: 'Top',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'right',
      type: 'responsiveDimension',
      label: 'Right',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'bottom',
      type: 'responsiveDimension',
      label: 'Bottom',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'left',
      type: 'responsiveDimension',
      label: 'Left',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'zIndex',
      type: 'number',
      label: 'Z-Index',
      category: 'Position',
      placeholder: 'auto',
      showWhen: { field: 'position', notEquals: 'static' },
    },

    {
      key: 'customCSS',
      type: 'customCSS',
      label: 'Custom CSS',
      category: 'Advanced',
      default: '',
    },

    // Flex Child sizing
    {
      key: 'flexChild',
      type: 'flexChild',
      label: 'Flex Child',
      category: 'Layout',
      showWhenParent: { field: 'display', in: ['flex', 'inline-flex'] },
      description: 'Controls sizing when inside a flex container',
      default: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
    },
  ],

  // Render function - will be implemented in the unified renderer
  render: null,
};

export default linkComponent;
